import React, { useState, useEffect, useMemo, useCallback } from "react"
import { Table, Col, Row, Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner } from "reactstrap"
import api from "API/Api"
import { Link } from "react-router-dom"
import SweetAlert from "react-bootstrap-sweetalert"
import loader from "./../../assets/loading/loader.gif"
import BulkImportExport from "../../components/BulkImportExport"
import { usePermissions } from "API/PermissionContext"

const Producers = () => {
  const { canAdd, canEdit, canDelete, canExport, MODULE } = usePermissions()
  const MID = MODULE.PRODUCERS
  const [producers, setProducers] = useState([])
  const [dataLoaded, setDataLoaded] = useState(false)
  const [checkedItems, setCheckedItems] = useState({})
  const [parentChecked, setParentChecked] = useState(false)
  const [confirm_alert, setconfirm_alert] = useState(false)
  const [confirm_multiple_alert, setconfirm_multiple_alert] = useState(false)
  const [success_dlg, setsuccess_dlg] = useState(false)
  const [dynamic_title, setdynamic_title] = useState("")
  const [dynamic_description, setdynamic_description] = useState("")
  const [producerId, setProducerId] = useState("")
  const [search, setSearch] = useState("")
  const [showFilter, setShowFilter] = useState(false)
  const [filterPlant, setFilterPlant] = useState("")
  const [filterCC, setFilterCC] = useState("")
  const [filterRoute, setFilterRoute] = useState("")
  const [filterCenter, setFilterCenter] = useState("")
  const [filterName, setFilterName] = useState("")
  const [filterCompany, setFilterCompany] = useState("")
  // Details & History modals
  const [detailsModal, setDetailsModal] = useState(false)
  const [historyModal, setHistoryModal] = useState(false)
  const [selectedProducer, setSelectedProducer] = useState(null)
  const [historyData, setHistoryData] = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)

  const fetchData = useCallback(async () => {
    try {
      const resp = await api.get("producers")
      setProducers(Array.isArray(resp.data.data) ? resp.data.data : resp.data.data?.data || [])
    } catch (e) { console.error(e) }
    setDataLoaded(true)
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const handleDelete = async () => {
    try { await api.delete("delete-producer/" + producerId); fetchData() } catch (e) { console.error(e) }
  }
  const handleMultiDelete = async () => {
    const ids = Object.keys(checkedItems).filter(k => checkedItems[k])
    if (!ids.length) return
    try { await api.post("delete-multiple-producers", { items: ids }); setCheckedItems({}); setParentChecked(false); fetchData() } catch (e) { console.error(e) }
  }
  const handleCheckbox = id => {
    setCheckedItems(prev => {
      const n = { ...prev, [id]: !prev[id] }
      setParentChecked(producers.length > 0 && producers.every(p => n[p.id]))
      return n
    })
  }
  const handleParentCheckbox = () => {
    const n = !parentChecked; setParentChecked(n)
    const items = {}; producers.forEach(p => { items[p.id] = n }); setCheckedItems(items)
  }
  const selectedCount = Object.values(checkedItems).filter(Boolean).length

  // Open Details modal
  const openDetails = (p) => { setSelectedProducer(p); setDetailsModal(true) }
  // Open History modal
  const openHistory = async (p) => {
    setSelectedProducer(p); setHistoryModal(true); setHistoryLoading(true)
    try {
      const resp = await api.get("producer-updates/" + p.id)
      setHistoryData(Array.isArray(resp.data.data) ? resp.data.data : resp.data.data?.data || [])
    } catch (e) { setHistoryData([]) }
    setHistoryLoading(false)
  }

  // Multiple companies check
  const multipleCompanies = useMemo(() => new Set(producers.map(p => p.companyName).filter(Boolean)).size > 1, [producers])

  // Unique filter options
  const uniqueCompanies = useMemo(() => [...new Set(producers.map(p => p.companyName).filter(Boolean))].sort(), [producers])
  const uniquePlants = useMemo(() => [...new Set(producers.map(p => p.plantName).filter(Boolean))].sort(), [producers])
  const uniqueCCs = useMemo(() => [...new Set(producers.map(p => p.chillingCenterName).filter(Boolean))].sort(), [producers])
  const uniqueRoutes = useMemo(() => [...new Set(producers.map(p => p.routeName).filter(Boolean))].sort(), [producers])
  const uniqueCenters = useMemo(() => [...new Set(producers.map(p => p.centerName).filter(Boolean))].sort(), [producers])

  const filtered = useMemo(() => {
    return producers.filter(p => {
      if (filterCompany && p.companyName !== filterCompany) return false
      if (filterPlant && p.plantName !== filterPlant) return false
      if (filterCC && p.chillingCenterName !== filterCC) return false
      if (filterRoute && p.routeName !== filterRoute) return false
      if (filterCenter && p.centerName !== filterCenter) return false
      if (filterName && !(p.producerName || "").toLowerCase().includes(filterName.toLowerCase())) return false
      if (search) {
        const s = search.toLowerCase()
        return (p.producerName || "").toLowerCase().includes(s) ||
          (p.producerNumber || "").toLowerCase().includes(s) ||
          (p.village || "").toLowerCase().includes(s) ||
          (p.centerName || "").toLowerCase().includes(s) ||
          (p.routeName || "").toLowerCase().includes(s) ||
          (p.plantName || "").toLowerCase().includes(s) ||
          (p.producerMobile || p.mobile || "").toLowerCase().includes(s)
      }
      return true
    })
  }, [producers, search, filterCompany, filterPlant, filterCC, filterRoute, filterCenter, filterName])

  const colSpan = (multipleCompanies ? 14 : 13)

  const removeFilter = () => {
    setFilterCompany(""); setFilterPlant(""); setFilterCC(""); setFilterRoute(""); setFilterCenter(""); setFilterName("")
    setShowFilter(!showFilter)
  }

  const summaryCards = [
    { label: "Total Producers", value: producers.length, icon: "fa-users", color: "#6366f1" },
    { label: "Collection Centers", value: new Set(producers.map(p => p.centerName).filter(Boolean)).size, icon: "fa-store-alt", color: "#0ea5e9" },
    { label: "Routes", value: new Set(producers.map(p => p.routeName).filter(Boolean)).size, icon: "fa-route", color: "#22c55e" },
  ]

  const milkLabel = (v) => { if (v === "1" || v === 1) return "Cow"; if (v === "2" || v === 2) return "Buffalo"; return "None" }
  const payLabel = (v) => (v === "1" || v === 1) ? "Bank Account" : "Cash"

  const actionBtnStyle = (bg) => ({
    width: "28px", height: "28px", borderRadius: "4px", background: bg, color: "#fff",
    border: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "12px", cursor: "pointer"
  })

  return (
    <>
      <div className="dashBody">
        <div className="font-size-18 mb-0 dashHeader" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <h4 style={{ margin: 0 }}>Producers</h4>
            {canAdd(MID) && (
              <Link to="/add-producer" title="Add Producer"
                style={{ ...actionBtnStyle("#28a745"), textDecoration: "none", width: "32px", height: "32px", fontSize: "14px" }}>
                <i className="fas fa-plus"></i>
              </Link>
            )}
            {canDelete(MID) && (
              <button title={selectedCount > 0 ? `Delete ${selectedCount} selected` : "Select items to delete"}
                onClick={() => { if (selectedCount > 0) setconfirm_multiple_alert(true); else alert("Please select at least one producer to delete.") }}
                style={{ ...actionBtnStyle("#dc3545"), width: "32px", height: "32px", fontSize: "14px", opacity: selectedCount > 0 ? 1 : 0.7 }}>
                <i className="fas fa-trash"></i>
              </button>
            )}
            <button title="Toggle Filter" onClick={removeFilter}
              style={{ ...actionBtnStyle("#0d6efd"), width: "32px", height: "32px", fontSize: "14px" }}>
              <i className="fas fa-filter"></i>
            </button>
            <BulkImportExport
              data={producers}
              columns={[
                { key: "producerName", label: "Producer Name", required: true },
                { key: "producerNumber", label: "Producer Number", required: true },
                { key: "collectionCenter", label: "Collection Center ID", required: true },
                { key: "chillingCenter", label: "Chilling Center ID", required: true },
                { key: "route", label: "Route ID", required: true },
                { key: "producerMobile", label: "Mobile" },
                { key: "village", label: "Village" },
                { key: "producerGender", label: "Gender", required: true },
                { key: "paymentMode", label: "Payment Mode (0=Cash, 1=Bank)", required: true },
              ]}
              sampleData={[{ producerName: "John Doe", producerNumber: "P001", collectionCenter: "1", chillingCenter: "1", route: "1", producerMobile: "9876543210", producerGender: "male", paymentMode: "0" }]}
              apiEndpoint="create-producer"
              entityName="Producers"
              onImportComplete={fetchData}
              showExport={canExport(MID)}
              showImport={canAdd(MID)}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <input type="text" className="form-control form-control-sm" placeholder="Search"
              style={{ width: "180px", borderRadius: "4px 0 0 4px" }}
              value={search} onChange={e => setSearch(e.target.value)} />
            <button className="btn btn-primary btn-sm" style={{ borderRadius: "0 4px 4px 0", height: "31px" }}>
              <i className="fas fa-search"></i>
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <Row className="mb-3 mt-2">
          {summaryCards.map((card, i) => (
            <Col md={3} sm={6} key={i}>
              <div style={{
                background: "#fff", borderRadius: "10px", padding: "14px 16px",
                borderLeft: `4px solid ${card.color}`, boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px"
              }}>
                <div style={{ width: "40px", height: "40px", borderRadius: "8px", background: card.color + "15",
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className={`fas ${card.icon}`} style={{ color: card.color, fontSize: "16px" }}></i>
                </div>
                <div>
                  <div style={{ fontSize: "20px", fontWeight: "700", color: "#1e293b" }}>{card.value}</div>
                  <div style={{ fontSize: "11px", color: "#6c757d", textTransform: "uppercase", letterSpacing: "0.5px" }}>{card.label}</div>
                </div>
              </div>
            </Col>
          ))}
        </Row>

        <div className="table-responsive">
          <Table className="table mb-0 dairyTbl" id="producersTbl">
            <thead>
              <tr>
                <th><input type="checkbox" checked={parentChecked} onChange={handleParentCheckbox} /></th>
                <th>#</th>
                {multipleCompanies && <th>Company</th>}
                <th>Plant</th>
                <th>Chilling Center</th>
                <th>Route</th>
                <th>Collection Center</th>
                <th>Member Number</th>
                <th>Member Name</th>
                <th>Mobile</th>
                <th>Farmer App</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {showFilter && (
                <tr>
                  <td></td><td></td>
                  {multipleCompanies && (
                    <td>
                      <select className="form-control form-control-sm" value={filterCompany} onChange={e => setFilterCompany(e.target.value)}>
                        <option value="">All</option>
                        {uniqueCompanies.map((c, i) => <option key={i} value={c}>{c}</option>)}
                      </select>
                    </td>
                  )}
                  <td>
                    <select className="form-control form-control-sm" value={filterPlant} onChange={e => setFilterPlant(e.target.value)}>
                      <option value="">All</option>
                      {uniquePlants.map((p, i) => <option key={i} value={p}>{p}</option>)}
                    </select>
                  </td>
                  <td>
                    <select className="form-control form-control-sm" value={filterCC} onChange={e => setFilterCC(e.target.value)}>
                      <option value="">All</option>
                      {uniqueCCs.map((c, i) => <option key={i} value={c}>{c}</option>)}
                    </select>
                  </td>
                  <td>
                    <select className="form-control form-control-sm" value={filterRoute} onChange={e => setFilterRoute(e.target.value)}>
                      <option value="">All</option>
                      {uniqueRoutes.map((r, i) => <option key={i} value={r}>{r}</option>)}
                    </select>
                  </td>
                  <td>
                    <select className="form-control form-control-sm" value={filterCenter} onChange={e => setFilterCenter(e.target.value)}>
                      <option value="">All</option>
                      {uniqueCenters.map((c, i) => <option key={i} value={c}>{c}</option>)}
                    </select>
                  </td>
                  <td></td>
                  <td><input type="text" className="form-control form-control-sm" placeholder="Name..."
                    value={filterName} onChange={e => setFilterName(e.target.value)} /></td>
                  <td></td><td></td><td></td><td></td>
                </tr>
              )}
              {!dataLoaded ? (
                <tr><td colSpan={colSpan}><img className="loaderImg" src={loader} alt="Loading" /></td></tr>
              ) : filtered.length > 0 ? (
                filtered.map((data, i) => (
                  <tr key={data.id}>
                    <td><input type="checkbox" checked={!!checkedItems[data.id]} onChange={() => handleCheckbox(data.id)} /></td>
                    <td>{i + 1}</td>
                    {multipleCompanies && (
                      <td><span style={{ background: '#f59e0b15', color: '#f59e0b', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>{data.companyName || 'Unassigned'}</span></td>
                    )}
                    <td><span style={{ background: '#6366f115', color: '#6366f1', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>{data.plantName || 'Unassigned'}</span></td>
                    <td><span style={{ background: '#0ea5e915', color: '#0ea5e9', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>{data.chillingCenterName || 'Unassigned'}</span></td>
                    <td><span style={{ background: '#22c55e15', color: '#22c55e', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>{data.routeName || 'Unassigned'}</span></td>
                    <td><span style={{ background: '#f59e0b15', color: '#f59e0b', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>{data.centerName || 'Unassigned'}</span></td>
                    <td><span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#64748b' }}>{data.producerNumber || "-"}</span></td>
                    <td style={{ fontWeight: "600" }}>{data.producerName}</td>
                    <td>{data.producerMobile || data.mobile || "—"}</td>
                    {/* Farmer App */}
                    <td>
                      <span style={{
                        background: data.farmerApp === 1 || data.farmerApp === "1" || data.farmer_app === 1 ? "#dcfce7" : "#fef2f2",
                        color: data.farmerApp === 1 || data.farmerApp === "1" || data.farmer_app === 1 ? "#16a34a" : "#dc2626",
                        padding: "2px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: 600
                      }}>{data.farmerApp === 1 || data.farmerApp === "1" || data.farmer_app === 1 ? "Yes" : "No"}</span>
                    </td>
                    <td>
                      <span style={{
                        background: (data.status === "inactive") ? "#fef2f2" : "#dcfce7",
                        color: (data.status === "inactive") ? "#dc2626" : "#16a34a",
                        padding: '3px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: 600
                      }}>{(data.status === "inactive") ? "Inactive" : "Active"}</span>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: "3px" }}>
                        {canEdit(MID) && <Link title="Edit" to={"/edit-producer/" + data.id} style={actionBtnStyle("#0d6efd")}>
                          <i className="fas fa-edit"></i></Link>}
                        <button title="Details" onClick={() => openDetails(data)} style={actionBtnStyle("#6366f1")}>
                          <i className="fas fa-eye"></i>
                        </button>
                        <button title="Update History" onClick={() => openHistory(data)} style={actionBtnStyle("#f59e0b")}>
                          <i className="fas fa-history"></i>
                        </button>
                        {canDelete(MID) && (
                          <button title="Delete" onClick={() => { setProducerId(data.id); setconfirm_alert(true) }} style={actionBtnStyle("#dc3545")}>
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={colSpan} className="text-center py-4">
                  <i className="fas fa-users fa-2x text-muted mb-2 d-block"></i>No producers found
                </td></tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {/* ── Details Modal ── */}
      <Modal isOpen={detailsModal} toggle={() => setDetailsModal(false)} size="lg">
        <ModalHeader toggle={() => setDetailsModal(false)}>Details</ModalHeader>
        <ModalBody>
          {selectedProducer && (
            <Row>
              <Col md={6}>
                <h6 style={{ fontWeight: 700, borderBottom: "1px solid #e2e8f0", paddingBottom: "6px", marginBottom: "10px" }}>Profile Details</h6>
                <p><strong>Producer Number :</strong> {selectedProducer.producerNumber || "N/A"}</p>
                <p><strong>Producer Short Number :</strong> {selectedProducer.shortNo || ""}</p>
                <p><strong>Producer Name :</strong> {selectedProducer.producerName}</p>
                <p><strong>Gender :</strong> {selectedProducer.producerGender || ""}</p>
                <p><strong>Aadhar :</strong> {selectedProducer.producerAadhar || ""}</p>
                <p><strong>PAN :</strong> {selectedProducer.producerPAN || ""}</p>
                <p><strong>Default Milk Type :</strong> {milkLabel(selectedProducer.milkType)}</p>
                <h6 style={{ fontWeight: 700, borderBottom: "1px solid #e2e8f0", paddingBottom: "6px", marginBottom: "10px", marginTop: "16px" }}>Contact Details</h6>
                <p><strong>Mobile :</strong> {selectedProducer.producerMobile || selectedProducer.mobile || ""}</p>
                <p><strong>Email :</strong> {selectedProducer.producerEmail || ""}</p>
                <p><strong>Address :</strong> {[selectedProducer.door, selectedProducer.village, selectedProducer.taluk].filter(Boolean).join(", ") || ""}</p>
              </Col>
              <Col md={6}>
                <h6 style={{ fontWeight: 700, borderBottom: "1px solid #e2e8f0", paddingBottom: "6px", marginBottom: "10px" }}>Payment & Account Details</h6>
                <p><strong>Payment Mode :</strong> {payLabel(selectedProducer.paymentMode)}</p>
                <h6 style={{ fontWeight: 700, borderBottom: "1px solid #e2e8f0", paddingBottom: "6px", marginBottom: "10px", marginTop: "16px" }}>Personal Details</h6>
                <p><strong>Deposit Amount :</strong> {selectedProducer.depositAmount || "0.00"}</p>
                <p><strong>No Of Cows :</strong> {selectedProducer.totalCows || 0}</p>
                <p><strong>No Of Buffalos :</strong> {selectedProducer.totalBuffalos || 0}</p>
                <p><strong>Agriculture Land :</strong> {selectedProducer.agricultureLand || ""}</p>
                <h6 style={{ fontWeight: 700, borderBottom: "1px solid #e2e8f0", paddingBottom: "6px", marginBottom: "10px", marginTop: "16px" }}>Remarks</h6>
                <p>{selectedProducer.remarks || ""}</p>
                <h6 style={{ fontWeight: 700, borderBottom: "1px solid #e2e8f0", paddingBottom: "6px", marginBottom: "10px", marginTop: "16px" }}>Documents</h6>
                <p><strong>Aadhaar :</strong> {selectedProducer.producerAadhar || "N/A"}</p>
                <p><strong>PAN :</strong> {selectedProducer.producerPAN || "N/A"}</p>
                <p><strong>Bank Passbook/Statement :</strong> N/A</p>
                <h6 style={{ fontWeight: 700, borderBottom: "1px solid #e2e8f0", paddingBottom: "6px", marginBottom: "10px", marginTop: "16px" }}>App</h6>
                <p><strong>Using Farmer App :</strong> {(selectedProducer.farmerApp === 1 || selectedProducer.farmerApp === "1" || selectedProducer.farmer_app === 1) ? "Yes" : "No"}</p>
              </Col>
            </Row>
          )}
        </ModalBody>
        <ModalFooter><Button color="secondary" onClick={() => setDetailsModal(false)}>Close</Button></ModalFooter>
      </Modal>

      {/* ── Update History Modal ── */}
      <Modal isOpen={historyModal} toggle={() => setHistoryModal(false)} size="lg">
        <ModalHeader toggle={() => setHistoryModal(false)}>{selectedProducer?.producerName || "Producer"}</ModalHeader>
        <ModalBody>
          <h6 style={{ background: "#f1f5f9", padding: "6px 12px", borderRadius: "4px", fontSize: "13px" }}>Producer Update History</h6>
          {historyLoading ? (
            <div className="text-center p-4"><Spinner color="primary" /></div>
          ) : historyData.length > 0 ? (
            <div className="table-responsive">
              <Table className="table table-bordered table-sm" style={{ fontSize: "13px" }}>
                <thead style={{ background: "#f8fafc" }}>
                  <tr>
                    <th>Producer Name</th>
                    <th>Producer Number</th>
                    <th>Updated By</th>
                    <th>Update Time</th>
                    <th>Changes</th>
                  </tr>
                </thead>
                <tbody>
                  {historyData.map((h, i) => (
                    <tr key={i}>
                      <td>{h.producerName || selectedProducer?.producerName || ""}</td>
                      <td>{h.producerNumber || selectedProducer?.producerNumber || ""}</td>
                      <td>{h.updated_by || ""}</td>
                      <td>{h.update_time || ""}</td>
                      <td>
                        {h.changes && typeof h.changes === "object" ? (
                          <div style={{ maxHeight: "120px", overflowY: "auto" }}>
                            {Object.entries(h.changes).map(([field, vals], j) => (
                              <div key={j} style={{ fontSize: "11px", marginBottom: "3px", padding: "2px 6px", background: "#f8fafc", borderRadius: "3px", borderLeft: "3px solid #6366f1" }}>
                                <strong style={{ color: "#334155" }}>{field}</strong>:{" "}
                                <span style={{ color: "#dc2626", textDecoration: "line-through" }}>{vals.from || "—"}</span>
                                {" → "}
                                <span style={{ color: "#16a34a", fontWeight: 600 }}>{vals.to || "—"}</span>
                              </div>
                            ))}
                          </div>
                        ) : <span className="text-muted">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <p className="text-muted text-center py-3">No update history available.</p>
          )}
        </ModalBody>
        <ModalFooter><Button color="secondary" onClick={() => setHistoryModal(false)}>Close</Button></ModalFooter>
      </Modal>

      {confirm_alert && (
        <SweetAlert title="Are you sure?" warning showCancel confirmBtnBsStyle="success" cancelBtnBsStyle="danger"
          onConfirm={() => { handleDelete(); setconfirm_alert(false); setsuccess_dlg(true); setdynamic_title("Deleted"); setdynamic_description("Producer deleted.") }}
          onCancel={() => setconfirm_alert(false)}>You won't be able to revert this!</SweetAlert>
      )}
      {confirm_multiple_alert && (
        <SweetAlert title={`Delete ${selectedCount} producers?`} warning showCancel confirmBtnBsStyle="success" cancelBtnBsStyle="danger"
          onConfirm={() => { handleMultiDelete(); setconfirm_multiple_alert(false); setsuccess_dlg(true); setdynamic_title("Deleted"); setdynamic_description("Producers deleted.") }}
          onCancel={() => setconfirm_multiple_alert(false)}>You won't be able to revert this!</SweetAlert>
      )}
      {success_dlg && (
        <SweetAlert success title={dynamic_title} onConfirm={() => setsuccess_dlg(false)}>{dynamic_description}</SweetAlert>
      )}
    </>
  )
}

export default Producers
